/**
 * /el/guides/lista-kalesmenon-gamou
 *
 * The Greek counterpart of `how-to-organize-wedding-guest-list.en.js` — the
 * article the RSVP cluster is built on, since the guides on either side of it
 * assume the list already exists.
 *
 * The sequence is the English article's and every section is one layer of it:
 *
 *   όριο καλεσμένων → άνθρωποι → προσκλήσεις ανά σπίτι → πρόσωπα → κανόνες
 *   (συνοδοί, παιδιά) → κατάσταση πρόσκλησης → απάντηση → τελικός αριθμός
 *
 * An edit that turns any section into a standalone tip has broken the sequence,
 * which is the piece.
 *
 * ── Where the Greek article differs, and why ─────────────────────────────
 *  - **Οι γονείς.** In the English original this is one section among sixteen.
 *    In Greece it is frequently the largest single force acting on the list —
 *    the parents' names are expected, often numerous, and often attached to
 *    money. The section is therefore written with more room and more care, and
 *    still as a DIVISION OF LABOUR rather than a confrontation: proposing a
 *    name is open to everyone, the total belongs to whoever is planning and
 *    paying. No section of this article may be turned into an argument for
 *    refusing a family.
 *  - **Οι κουμπάροι** replace "the wedding party", which is not a Greek
 *    category.
 *  - **Τα παιδιά.** Children at a Greek wedding are the default rather than
 *    the exception, so the adults-only option is presented as a real choice
 *    that has to be said clearly and early — not as a neutral fourth item.
 *  - **Το χωριό.** Two entirely ordinary Greek cases the English list has no
 *    reason to carry: guests invited through a family rather than by name, and
 *    a wedding where a whole village is in some sense invited. Both are
 *    counting problems, and both are answered by the household layer.
 *
 * ── The hedges, which are load-bearing ───────────────────────────────────
 * There is no correct guest count, no correct plus-one policy, no correct
 * children policy. Every section that touches one says so and offers a common
 * approach rather than a rule. What the article WILL assert is process: decide
 * before you need it, apply it evenly, keep one record.
 *
 * Banned outright: «πρέπει να καλέσετε», «μην καλέσετε ποτέ», etiquette stated
 * as law, guilt as an argument, any assumption about how a reader's family is
 * arranged, and any formula that turns a budget into a number of people.
 *
 * ── The claims this guide is allowed to make ─────────────────────────────
 * Four, all in the closing section:
 *
 *   - «Η λίστα καλεσμένων και τα εργαλεία RSVP είναι διαθέσιμα στο Heirloom.»
 *   - invited guests and the people on each invitation live in one list
 *   - the couple records who has been invited
 *   - a companion a guest asks to bring arrives as a request beside them, and
 *     counts once the couple approves it
 *
 * And what it must never say, because the product does not do it: generate a
 * guest list, decide who is invited, send reminders on its own, merge
 * duplicates by itself, or produce a seating plan without the couple. Guests do
 * not edit the couple's list.
 *
 * No copy in this file contains "&", "<" or ">".
 */

const howToOrganizeWeddingGuestListEl = {
  slug: "lista-kalesmenon-gamou",

  title: "Λίστα καλεσμένων γάμου: πώς να την οργανώσετε",
  metaTitle: "Λίστα καλεσμένων γάμου: πώς να την οργανώσετε | Agapaé",
  metaDescription:
    "Πώς οργανώνεται η λίστα καλεσμένων: το όριο ατόμων, οι προσκλήσεις ανά σπίτι, οι συνοδοί, τα παιδιά, τα ονόματα των γονιών και το πώς κόβεται όταν χρειαστεί.",

  eyebrow: "Λίστα καλεσμένων",

  hero: {
    src: "/assets/images/invitation/invitation-3.jpg",
    alt: "Ασπρόμαυρη φωτογραφία νύφης και γαμπρού που περπατούν δίπλα δίπλα σε ένα ηλιόλουστο χωράφι, με τη νύφη να κρατά μπουκέτο, έναν ξύλινο φράχτη πίσω τους και λίγους ανθρώπους μακριά στο μονοπάτι",
    width: 6558,
    height: 3659,
  },

  intro: [
    `Σχεδόν όλα στον γάμο περιμένουν έναν αριθμό. Ο χώρος, ο προϋπολογισμός, τα προσκλητήρια, το φαγητό και τα τραπέζια εξαρτώνται από το πόσοι έρχονται — και από το ποιοι είναι αυτοί.`,
    `Γι' αυτό και μια λίστα που ξεκινά ως μια σελίδα με ονόματα γίνεται γρήγορα πρόβλημα: τα ονόματα μπαίνουν εύκολα και βγαίνουν δύσκολα.`,
    `Ο τρόπος να μη σας ξεφύγει είναι να την οργανώσετε σε στρώματα: το όριο που έχετε συμφωνήσει, οι άνθρωποι μέσα σε αυτό, τα σπίτια στα οποία ανήκουν, οι κανόνες που κρίνουν ποιοι άλλοι μπαίνουν και η κατάσταση κάθε πρόσκλησης.`,
    `Πρώτα τα όρια, μετά οι άνθρωποι μέσα τους. Έτσι η λίστα παραμένει χρήσιμη από το πρώτο πρόχειρο μέχρι τον τελικό αριθμό.`,
  ],

  sections: [
    {
      heading: "Ξεκινήστε από τον αριθμό, όχι από τα ονόματα",
      content: [
        `Πριν γραφτεί το πρώτο όνομα, συμφωνήστε χονδρικά πόσο μεγάλος μπορεί να είναι ο γάμος.`,
        `Το όριο αυτό το διαμορφώνουν λίγα πρακτικά πράγματα:`,
        {
          type: "list",
          items: [
            `τι χωράει ο χώρος σας`,
            `ο προϋπολογισμός, αφού τα περισσότερα κόστη μετριούνται κατ' άτομο`,
            `η ατμόσφαιρα που θέλετε`,
            `περιορισμοί που δεν διάλεξε κανείς, όπως η απόσταση ή η διαμονή`,
          ],
        },
        `Δεν υπάρχει σωστός αριθμός, ούτε τύπος που τον βγάζει. Ένας μικρός γάμος δεν είναι συμβιβασμός και ένας μεγάλος δεν είναι υπερβολή.`,
        `Σημασία έχει να υπάρχει το όριο πριν από τη λίστα. Ένα όριο που συμφωνείται μετά δεν είναι όριο· είναι διαπραγμάτευση.`,
      ],
    },
    {
      heading: "Μία λίστα, και μόνο μία",
      content: [
        `Η λίστα καλεσμένων χαλάει πολύ πριν κοπεί κάποιος από αυτήν: χαλάει όταν αρχίσουν να υπάρχουν περισσότερες από μία.`,
        `Τα ονόματα μαζεύονται εκεί όπου πρωτοειπώθηκαν:`,
        {
          type: "list",
          items: [
            `σημειώσεις στο κινητό`,
            `μηνύματα ανάμεσά σας`,
            `ένα αρχείο Excel που ξεκίνησε ο ένας από τους δύο`,
            `ένα τετράδιο που κρατά μια μαμά`,
          ],
        },
        `Το καθένα έχει δίκιο σε κάτι και άδικο στο σύνολο, και η διαφωνία βγαίνει στην επιφάνεια τη μέρα που παραγγέλνετε προσκλητήρια ή δίνετε αριθμό στο catering.`,
        `Κρατήστε μία λίστα που είναι η επίσημη. Excel, κοινό έγγραφο ή ένα εργαλείο φτιαγμένο γι' αυτό — το εργαλείο μετράει πολύ λιγότερο από τον κανόνα ότι η λίστα είναι μία και είναι η σωστή.`,
      ],
    },
    {
      heading: "Γράψτε πρώτα όσους ξέρετε ότι θέλετε",
      content: [
        `Το πρώτο πρόχειρο πρέπει να είναι γενναιόδωρο, όχι τελικό. Είναι πιο εύκολο να σβήσετε ένα όνομα παρά να θυμηθείτε ένα που δεν γράψατε ποτέ.`,
        `Τα περισσότερα ζευγάρια περνούν από κάτι σαν αυτό:`,
        {
          type: "list",
          items: [
            `στενή οικογένεια`,
            `συγγενείς`,
            `οι πιο κοντινοί φίλοι`,
            `οι ευρύτερες παρέες`,
            `συνάδελφοι`,
            `φίλοι της οικογένειας`,
            `ονόματα που θα ήθελαν οι γονείς σας να συζητηθούν`,
          ],
        },
        `Δεν χωράει κάθε κατηγορία σε κάθε γάμο, και καμία δεν σας δεσμεύει: κάποιοι δεν καλούν κανέναν συνάδελφο, άλλοι όλο το γραφείο.`,
        `Αυτό το πρόχειρο δεν είναι η λίστα των προσκλήσεων· είναι η δεξαμενή από την οποία θα βγει.`,
      ],
    },
    {
      heading: "Βάλτε προτεραιότητες αντί να αποφασίζετε ένα ένα",
      content: [
        `Η δυσκολία είναι σχεδόν πάντα σε λίγες οριακές περιπτώσεις, και το να τις λύνετε επιτόπου είναι που κάνει τη δουλειά να μοιάζει ατελείωτη.`,
        `Μια απλή σήμανση προχωρά πολύ πιο γρήγορα:`,
        {
          type: "list",
          items: [
            `Πρώτη προτεραιότητα: όσοι πρέπει να είναι εκεί`,
            `Δεύτερη: όσους θα θέλατε, αν χωρέσουν`,
            `Τρίτη: όσους θα χαιρόσασταν να έχετε, αν το επιτρέψει ο αριθμός`,
          ],
        },
        `Σε αυτό το στάδιο δεν απορρίπτεται κανείς. Κάποιος στην τρίτη προτεραιότητα περιμένει έναν αριθμό που δεν έχετε ακόμη.`,
        `Κάποια ζευγάρια το κάνουν πιο επίσημα, με μια δεύτερη λίστα από την οποία καλούν καθώς έρχονται αρνήσεις. Δουλεύει, με προσοχή: κανείς δεν πρέπει να μπορεί να καταλάβει ότι κάλυψε τη θέση κάποιου άλλου.`,
      ],
    },
    {
      heading: "Αποφασίστε για τους συνοδούς πριν σας χρειαστεί",
      content: [
        `Οι συνοδοί μετακινούν τον αριθμό περισσότερο από κάθε άλλη απόφαση. Είκοσι προσκλήσεις με συνοδό είναι είκοσι επιπλέον άτομα στο τραπέζι.`,
        `Τα ζευγάρια συνήθως ζυγίζουν:`,
        {
          type: "list",
          items: [
            `όσους είναι παντρεμένοι, αρραβωνιασμένοι ή σε μακροχρόνια σχέση`,
            `τους κουμπάρους`,
            `όσους αλλιώς δεν θα γνώριζαν σχεδόν κανέναν στην αίθουσα`,
          ],
        },
        `Κανένα από αυτά δεν είναι κανόνας: ένας γάμος που δίνει συνοδό σε όλους δεν είναι πιο γενναιόδωρος από έναν που δεν δίνει σε κανέναν, απλώς έχει άλλο μέγεθος.`,
        `Αυτό που μετράει πρακτικά είναι η συνέπεια: αποφασίστε πριν φύγουν τα προσκλητήρια και εφαρμόστε το ίδιο σε όλους.`,
      ],
    },
    {
      heading: "Αποφασίστε τι θα κάνετε με τα παιδιά",
      content: [
        `Το ίδιο ισχύει και για τα παιδιά, με μία διαφορά: αυτή η απόφαση φτάνει στους καλεσμένους νωρίτερα, γιατί οι οικογένειες πρέπει να οργανώσουν τη μέρα τους γύρω από αυτήν.`,
        `Στους ελληνικούς γάμους τα παιδιά είναι συνήθως καλεσμένα, οπότε καθετί διαφορετικό πρέπει να ειπωθεί καθαρά και έγκαιρα.`,
        `Οι επιλογές είναι λίγες:`,
        {
          type: "list",
          items: [
            `τα παιδιά καλεσμένα, όπως συνήθως`,
            `μόνο τα παιδιά της στενής οικογένειας`,
            `γάμος μόνο για ενήλικες`,
            `κατά περίπτωση`,
          ],
        },
        `Καμία επιλογή δεν είναι πιο σωστή. Αυτό που παρατηρεί ο κόσμος δεν είναι η πολιτική σας· είναι η ασυνέπεια.`,
        `Ό,τι κι αν διαλέξετε, γράψτε το στο προσκλητήριο και καταγράψτε ανά πρόσκληση αν περιλαμβάνονται παιδιά και πόσα — γιατί αυτό θα χρειαστούν το catering και τα τραπέζια.`,
      ],
    },
    {
      heading: "Οργανώστε ανά σπίτι, όχι μόνο ανά όνομα",
      content: [
        `Αυτή είναι η δομή που κάνει όλα τα επόμενα ευκολότερα, και αυτή που λείπει από τις περισσότερες λίστες.`,
        `Δύο πράγματα πρέπει να φαίνονται ταυτόχρονα:`,
        {
          type: "list",
          items: [
            `η πρόσκληση, δηλαδή ποιοι παίρνουν ένα προσκλητήριο`,
            `τα πρόσωπα, δηλαδή ποιους καλύπτει αυτό το προσκλητήριο`,
          ],
        },
        `Ένα προσκλητήριο πηγαίνει στη Μαρία Παπαδοπούλου και στον Νίκο Παπαδόπουλο: μία πρόσκληση, δύο άτομα. Αν περιλαμβάνονται και τα δύο τους παιδιά, είναι πάλι μία πρόσκληση και τέσσερα άτομα. Μια πρόσκληση σε έναν καλεσμένο με συνοδό είναι μία πρόσκληση, ένα όνομα και μία θέση ακόμη αδιευκρίνιστη.`,
        `Κάθε φάκελος και κάθε μήνυμα μετράει προσκλήσεις. Κάθε αριθμός για το φαγητό και κάθε τραπέζι μετράει ανθρώπους. Μια λίστα που εκφράζει μόνο το ένα από τα δύο πρέπει να ξαναμετριέται στο χέρι κάθε φορά που χρειάζεστε το άλλο.`,
        `Και είναι η ίδια δομή που απαντά αργότερα στο ποιοι πάνε μαζί στο ίδιο τραπέζι.`,
      ],
    },
    {
      heading: "Οι δύσκολες περιπτώσεις στην Ελλάδα",
      content: [
        `Δύο περιπτώσεις είναι απολύτως συνηθισμένες εδώ και δεν χωράνε σε μια λίστα με ονόματα.`,
        `Η πρώτη είναι η πρόσκληση που πάει σε μια οικογένεια και όχι σε πρόσωπα: «οι Παπαδόπουλοι» μπορεί να είναι τρία άτομα ή επτά, και η διαφορά μετριέται σε τραπέζια.`,
        `Η δεύτερη είναι ο γάμος στο χωριό, όπου ένα μέρος της πρόσκλησης είναι ανοιχτό εξ ορισμού και ο ακριβής αριθμός δεν πρόκειται ποτέ να είναι απόλυτα ακριβής.`,
        `Δεν λύνονται με αυστηρότητα. Λύνονται με καταγραφή:`,
        {
          type: "list",
          items: [
            `γράψτε ονομαστικά όσους ξέρετε`,
            `γράψτε μια εκτίμηση για όσους δεν ξέρετε ακόμη, και σημειώστε ότι είναι εκτίμηση`,
            `ρωτήστε ευθέως εκεί που ο αριθμός αλλάζει τραπέζι`,
            `κρατήστε την εκτίμηση χωριστά από τους επιβεβαιωμένους`,
          ],
        },
        `Ένας αριθμός που ξέρετε ότι είναι κατά προσέγγιση είναι πολύ πιο χρήσιμος από έναν αριθμό που νομίζετε ότι είναι ακριβής.`,
      ],
    },
    {
      heading: "Κρατήστε τη σωστή πληροφορία, και όχι περισσότερη",
      content: [
        `Μια πρακτική λίστα καταλήγει συνήθως να κρατά:`,
        {
          type: "list",
          items: [
            `ονοματεπώνυμο`,
            `το σπίτι ή την πρόσκληση στην οποία ανήκει`,
            `από πού τον ξέρετε`,
            `στοιχεία επικοινωνίας, ανάλογα με το πώς θα τον καλέσετε`,
            `αν έχει σταλεί η πρόσκληση`,
            `αν η πρόσκληση περιλαμβάνει συνοδό`,
            `παιδιά και ηλικίες, όπου χρειάζεται`,
            `την απάντηση, μόλις αρχίσουν να έρχονται`,
            `σημειώσεις που επηρεάζουν την οργάνωση`,
            `το τραπέζι, πολύ αργότερα`,
          ],
        },
        `Όχι όλα μαζί. Τα περισσότερα είναι άχρηστα τον πρώτο μήνα και απαραίτητα τον ένατο, οπότε προσθέστε το καθένα όταν αρχίσει να δουλεύει.`,
        `Μαζέψτε ό,τι θα χρησιμοποιήσει ο γάμος σας και σταματήστε εκεί. Όλα αυτά είναι προσωπικά δεδομένα άλλων ανθρώπων, που τα κρατάτε για λογαριασμό τους, και μια στήλη που δεν ανοίγετε ποτέ είναι ένα ακόμη πράγμα που πρέπει να ενημερώνετε.`,
      ],
    },
    {
      heading: "Άλλο καλεσμένος και άλλο επιβεβαιωμένος",
      content: [
        `Η λίστα απαντά ποιους καλέσατε. Η απάντηση λέει ποιοι έρχονται. Κρατώντας τα χωριστά, η λίστα δεν διαλύεται σε έναν σωρό μηνυμάτων.`,
        `Ανά πάσα στιγμή, ένας καλεσμένος μπορεί να είναι:`,
        {
          type: "list",
          items: [
            `καλεσμένος`,
            `καλεσμένος και χωρίς απάντηση`,
            `επιβεβαιωμένος`,
            `έχει απαντήσει αρνητικά`,
          ],
        },
        `Η πρόσκληση είναι οριστική· η απάντηση δεν είναι, και μπορεί να αλλάξει περισσότερες από μία φορές. Η λίστα λοιπόν κρατά την πρόσκληση από κάτω και την απάντηση από πάνω: κάποιος που αρνήθηκε παραμένει κάποιος που καλέσατε.`,
        {
          type: "p",
          text: [
            `Μόλις φύγουν τα προσκλητήρια, αλλάζει η δουλειά: παύει να είναι χτίσιμο λίστας και γίνεται διαχείριση απαντήσεων. Από εκεί συνεχίζει το `,
            {
              text: "Πώς να οργανώσετε τα RSVP του γάμου χωρίς να χάσετε καλεσμένους",
              href: "/guides/how-to-organize-wedding-rsvps",
            },
            `.`,
          ],
        },
      ],
    },
    {
      heading: "Όταν οι γονείς βάζουν ονόματα",
      content: [
        `Στους περισσότερους ελληνικούς γάμους τα ονόματα δεν έρχονται μόνο από εσάς τους δύο. Οι γονείς προτείνουν, και συχνά συνεισφέρουν και οικονομικά.`,
        `Είναι απολύτως συνηθισμένο, και αντιμετωπίζεται πολύ πιο εύκολα ως διαδικασία παρά ως ζήτημα εξουσίας.`,
        `Βοηθά να συμφωνήσετε νωρίς και ξεκάθαρα:`,
        {
          type: "list",
          items: [
            `περίπου πόσα ονόματα μπορούν να προτείνουν`,
            `πού στέκονται αυτά απέναντι στις δικές σας προτεραιότητες`,
            `ότι ό,τι προτείνεται πρέπει να χωρέσει μέσα στον συνολικό αριθμό`,
          ],
        },
        `Η χρήσιμη διάκριση είναι ανάμεσα στο να προτείνεις ένα όνομα και στο να ορίζεις το σύνολο: η πρόταση είναι ανοιχτή σε όλους, το σύνολο ανήκει σε όσους οργανώνουν και πληρώνουν τη μέρα.`,
        `Και μια πρακτική λεπτομέρεια που γλιτώνει πολλή ένταση: τα ονόματα των γονιών μπαίνουν στην ίδια λίστα, σημειωμένα ως προτάσεις μέχρι να αποφασιστούν. Δύο ξεχωριστές λίστες γίνονται δύο ξεχωριστές αλήθειες.`,
      ],
    },
    {
      heading: "Πώς κόβεται μια λίστα",
      content: [
        `Σχεδόν κάθε λίστα ξεπερνά τον αριθμό της τουλάχιστον μία φορά. Το κόψιμο είναι δυσάρεστο — πολύ λιγότερο όμως όταν γίνεται με κανόνα και όχι με ετυμηγορίες.`,
        {
          type: "steps",
          items: [
            `Γυρίστε στο όριο και επιβεβαιώστε ότι είναι ακόμη ο αριθμός σας.`,
            `Αποφασίστε τα κριτήρια και εφαρμόστε τα σε όλους το ίδιο.`,
            `Ξεκινήστε από τη χαμηλότερη προτεραιότητα και όχι από όλη τη λίστα.`,
            `Ψάξτε για επικαλύψεις: ένα σπίτι γραμμένο δύο φορές, έναν συνοδό που είναι ήδη καλεσμένος.`,
            `Ξαναδείτε τους κανόνες για συνοδούς και παιδιά, αν δεν αποφασίστηκαν ποτέ ρητά· ο άγραφος κανόνας είναι συνήθως ο ακριβότερος.`,
            `Κάντε τις αλλαγές όσο δεν έχουν φύγει ακόμη τα προσκλητήρια.`,
          ],
        },
        `Λίγες ερωτήσεις κάνουν τη μισή δουλειά:`,
        {
          type: "list",
          items: [
            `Πότε μιλήσαμε τελευταία φορά ουσιαστικά;`,
            `Θα μας έλειπαν πραγματικά από τη μέρα;`,
            `Τους καλούμε επειδή τους θέλουμε ή από υποχρέωση;`,
            `Έχουμε σκεφτεί το ίδιο για όλους τους αντίστοιχους;`,
          ],
        },
        `Ένας κανόνας που εφαρμόζεται παντού το ίδιο είναι πολύ πιο εύκολος από μια σειρά ξεχωριστών αποφάσεων.`,
      ],
    },
    {
      heading: "Κρατήστε τη λίστα ζωντανή",
      content: [
        `Η λίστα δεν τελειώνει με το πρώτο πρόχειρο. Αλλάζει για απολύτως φυσιολογικούς λόγους:`,
        {
          type: "list",
          items: [
            `νέες σχέσεις`,
            `συνοδοί που αλλάζουν`,
            `αποφάσεις που ξανασκέφτεστε`,
            `αρνήσεις`,
            `προσθήκες της τελευταίας στιγμής`,
            `ακυρώσεις`,
            `αλλαγές μέσα στις οικογένειες`,
          ],
        },
        `Ενημερώστε το ίδιο αρχείο, μην κρατάτε αντίγραφα. Ένας φάκελος με «λίστα τελική» και «λίστα τελική 2» είναι ένας φάκελος όπου κανείς δεν ξέρει ποια ισχύει.`,
        `Ο έλεγχος είναι απλός. Αν σας ρωτούσε κάποιος σήμερα πόσοι έρχονται, θα μπορούσατε να απαντήσετε από τη λίστα χωρίς να ανοίξετε τίποτε άλλο;`,
      ],
    },
    {
      heading: "Πότε είναι έτοιμη η λίστα",
      content: [
        `Η λίστα είναι έτοιμη για προσκλητήρια και απαντήσεις όταν:`,
        {
          type: "list",
          items: [
            `το όριο έχει συμφωνηθεί`,
            `οι άνθρωποι που θα κληθούν έχουν αποφασιστεί`,
            `ο κανόνας για τους συνοδούς είναι ξεκάθαρος`,
            `ο κανόνας για τα παιδιά είναι ξεκάθαρος`,
            `τα σπίτια και οι προσκλήσεις φαίνονται μέσα στη λίστα`,
            `οι διπλοεγγραφές έχουν φύγει`,
            `τα στοιχεία επικοινωνίας φτάνουν για να σταλεί πρόσκληση`,
            `υπάρχει μία επίσημη εκδοχή της`,
          ],
        },
        `Έτοιμη δεν σημαίνει κλειστή: θα μπουν και θα βγουν άνθρωποι και μετά τα προσκλητήρια, και μια λίστα που το αντέχει αυτό δουλεύει όπως πρέπει.`,
      ],
    },
    {
      heading: "Από τη λίστα στις απαντήσεις και στα τραπέζια",
      content: [
        `Η λίστα είναι το πρώτο στάδιο μιας αλυσίδας, όπου κάθε κρίκος προσθέτει κάτι στον προηγούμενο:`,
        {
          type: "steps",
          items: [
            `Η λίστα αποφασίζει ποιοι καλούνται.`,
            `Τα προσκλητήρια φέρνουν την απόφαση μπροστά στους ανθρώπους.`,
            `Οι απαντήσεις καταγράφουν τι είπαν.`,
            `Οι επιβεβαιωμένοι γίνονται ο τελικός σας αριθμός.`,
            `Το πλάνο τους μοιράζει στα τραπέζια.`,
          ],
        },
        `Τίποτα από τα επόμενα δεν μπορεί να ανακτήσει ό,τι δεν κράτησε ποτέ η λίστα: αν τα σπίτια δεν ομαδοποιήθηκαν, θα ξαναφτιαχτούν στο χέρι τη μέρα που θα σχεδιάζετε τραπέζια.`,
        {
          type: "p",
          text: [
            `Αυτό το τελευταίο στάδιο είναι θέμα από μόνο του, και το `,
            {
              text: "Πλάνο τραπεζιών γάμου: πώς να το φτιάξετε βήμα βήμα",
              href: "/guides/wedding-seating-plan",
            },
            ` το αναλαμβάνει μόλις έρθουν οι απαντήσεις σας.`,
          ],
        },
      ],
    },
    {
      heading: "Όλη η δουλειά σε εννιά βήματα",
      content: [
        `Μαζεμένα, χωράνε σε μια σελίδα:`,
        {
          type: "steps",
          items: [
            `Ορίστε το όριο των καλεσμένων.`,
            `Γράψτε όλους όσους ίσως καλέσετε.`,
            `Βάλτε προτεραιότητες.`,
            `Αποφασίστε για συνοδούς και παιδιά.`,
            `Οργανώστε τους ανθρώπους σε προσκλήσεις και σπίτια.`,
            `Μαζέψτε τα πάντα σε μία λίστα.`,
            `Προσθέστε στοιχεία επικοινωνίας και πληροφορίες πρόσκλησης.`,
            `Στείλτε τα προσκλητήρια και ξεκινήστε να κρατάτε τις απαντήσεις.`,
            `Κρατήστε την ίδια λίστα ενημερωμένη μέχρι τον τελικό αριθμό.`,
          ],
        },
        `Η σειρά δουλεύει: τα βήματα ένα έως τέσσερα είναι αποφάσεις, τα πέντε έως εννιά είναι καταγραφή. Ανάποδα, η καταγραφή ξαναγίνεται κάθε φορά που αλλάζει μια απόφαση.`,
      ],
    },
    {
      heading: "Μια λίστα πάνω στην οποία μπορείτε να στηριχτείτε",
      content: [
        `Η καλύτερη λίστα δεν είναι η πιο αναλυτική. Είναι αυτή που απαντά, χωρίς να ανοίξετε τίποτε άλλο:`,
        {
          type: "list",
          items: [
            `ποιους καλούμε`,
            `ποιοι έχουν πράγματι κληθεί`,
            `ποιοι πάνε μαζί`,
            `πόσα άτομα είναι αυτό`,
            `τι μένει ανοιχτό`,
          ],
        },
        `Μια λίστα καλεσμένων είναι ταυτόχρονα έγγραφο οργάνωσης και ένα σύνολο προσωπικών αποφάσεων. Οι αποφάσεις είναι δικές σας· το έγγραφο απλώς πρέπει να τις κρατά καθαρές.`,
        {
          type: "note",
          text: `Η λίστα καλεσμένων και τα εργαλεία RSVP στο Agapaé είναι διαθέσιμα στο πακέτο Heirloom.`,
        },
        `Στο Agapaé η λίστα καλεσμένων είναι η λίστα των προσκλήσεων: κάθε πρόσκληση κρατά τα πρόσωπα που καλύπτει, εσείς καταγράφετε ποιοι έχουν κληθεί, και οι απαντήσεις έρχονται δίπλα στον άνθρωπο από τον οποίο ήρθαν. Ο συνοδός που ζητά κάποιος να φέρει εμφανίζεται ως αίτημα δίπλα του και μετράει μόλις τον εγκρίνετε.`,
        {
          type: "p",
          text: [
            `Για τη λίστα και τις απαντήσεις μέσα στο προϊόν, δείτε το `,
            { text: "RSVP γάμου", href: "/wedding-rsvp" },
            `.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Συχνές ερωτήσεις",

  faq: [
    {
      q: `Πότε να ξεκινήσω τη λίστα καλεσμένων;`,
      a: `Νωρίς, και συνήθως πριν κλείσετε χώρο, γιατί ο αριθμός και ο χώρος αποφασίζουν ο ένας τον άλλον. Ένα σκόπιμα πρόχειρο πρώτο σχέδιο αρκεί σε εκείνο το στάδιο: χρειάζεστε ρεαλιστικό μέγεθος, όχι τελική λίστα.`,
    },
    {
      q: `Πόσους πρέπει να καλέσω;`,
      a: `Δεν υπάρχει σωστός αριθμός ούτε τύπος που τον βγάζει. Η χρήσιμη εκδοχή της ερώτησης είναι πόσους μπορείτε να καλέσετε, και αυτό προκύπτει από τη χωρητικότητα του χώρου, τον προϋπολογισμό ανά άτομο και το είδος της μέρας που θέλετε.`,
    },
    {
      q: `Πώς οργανώνεται σωστά μια λίστα καλεσμένων;`,
      a: `Σε στρώματα και όχι ως μια επίπεδη σειρά ονομάτων: πρώτα ο αριθμός, μετά οι άνθρωποι, μετά τα σπίτια που καλύπτει κάθε πρόσκληση, μετά οι κανόνες για συνοδούς και παιδιά, και τέλος η κατάσταση της πρόσκλησης και της απάντησης.`,
    },
    {
      q: `Σε ποιους δίνω συνοδό;`,
      a: `Είναι δική σας απόφαση, όχι κανόνας. Πολλά ζευγάρια δίνουν συνοδό σε όσους είναι σε σχέση, στους κουμπάρους και σε όσους αλλιώς δεν θα γνώριζαν κανέναν. Σημασία έχει η συνέπεια: αποφασίστε πριν φύγουν τα προσκλητήρια και κρατήστε τον ίδιο κανόνα για όλους.`,
    },
    {
      q: `Τι πρέπει να γράφει η λίστα;`,
      a: `Όσα χρειάζονται για να αποφασίσετε και για να στείλετε πρόσκληση: όνομα, σπίτι ή πρόσκληση, από πού τον ξέρετε, στοιχεία επικοινωνίας, αν έχει σταλεί, αν περιλαμβάνει συνοδό, τα παιδιά όπου χρειάζεται και την απάντηση μόλις έρθει.`,
    },
  ],

  related: {
    heading: "Πού ταιριάζει αυτό στο Agapaé",

    guides: ["how-to-organize-wedding-rsvps", "when-should-wedding-guests-rsvp"],

    product: [
      {
        href: "/wedding-rsvp",
        label: "RSVP γάμου",
        blurb:
          "Η λίστα καλεσμένων και οι απαντήσεις μαζί: οι προσκλήσεις που στέλνετε, τα πρόσωπα που καλύπτει η καθεμία και οι απαντήσεις που ελέγχετε. Διαθέσιμο στο πακέτο Heirloom.",
      },
    ],
  },
};

export default howToOrganizeWeddingGuestListEl;
